import React, { useEffect, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import logo from "../resources/logo.png";

function Header() {
  const [toggleSidebar, setToggleSidebar] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const { pathname } = useLocation();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 20);
    window.addEventListener("scroll", onScroll);
    onScroll();
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => {
    setToggleSidebar(false);
  }, [pathname]);

  const navItems = [
    { to: "/", label: "Home" },
    { to: "/services", label: "Services" },
    { to: "/aboutus", label: "About Us" },
  ];

  const isActive = (path) =>
    path === "/" ? pathname === "/" : pathname.startsWith(path);

  return (
    <div className="sticky top-0 z-50">
      {/* Top utility bar */}
      <div className="font-chivo text-[11px] md:text-xs bg-zinc-950 text-zinc-300 border-b border-white/5">
        <div className="max-w-7xl mx-auto flex justify-between items-center px-4 md:px-8 py-2">
          <a
            href="https://www.google.com/maps/place/BiniAutoRepairINC/@40.9515721,-73.8751458,15z/data=!4m2!3m1!1s0x0:0x58aeec725b7b64cb?sa=X&ved=1t:2428&ictx=111"
            className="flex items-center gap-2 hover:text-white transition-colors"
            target="_blank"
            rel="noreferrer"
          >
            <i className="fa-solid fa-location-dot text-brand-500"></i>
            <span>10 Eastman Pl, Yonkers, NY</span>
          </a>
          <a
            href="tel:+13475820864"
            className="flex items-center gap-2 hover:text-white transition-colors"
          >
            <i className="fa-solid fa-phone text-brand-500"></i>
            <span>(347) 582-0864</span>
          </a>
          <a
            href="mailto:biniautorepairinc@gmail.com"
            className="hidden md:flex items-center gap-2 hover:text-white transition-colors"
          >
            <i className="fa-solid fa-envelope text-brand-500"></i>
            <span>biniautorepairinc@gmail.com</span>
          </a>
        </div>
      </div>

      {/* Main nav */}
      <nav
        className={`font-chivo w-full transition-all duration-300 border-b ${
          scrolled
            ? "bg-black/80 backdrop-blur-xl border-white/10 shadow-lg shadow-black/20"
            : "bg-black border-transparent"
        } text-white`}
      >
        <div className="max-w-7xl mx-auto flex justify-between items-center px-4 md:px-8 py-3">
          <Link to={"/"} className="flex items-center gap-3 group">
            <img
              src={logo}
              alt="Bini Autorepair"
              className="w-[150px] md:w-[180px] h-16 object-contain transition-transform group-hover:scale-105"
            />
          </Link>

          {/* Desktop nav */}
          <ul className="hidden md:flex items-center gap-2">
            {navItems.map((item) => (
              <li key={item.to}>
                <Link
                  to={item.to}
                  className={`relative px-5 py-2 text-sm uppercase tracking-wider font-semibold rounded-full transition-all ${
                    isActive(item.to)
                      ? "text-white bg-gradient-to-r from-brand-600 to-brand-800 shadow-glow"
                      : "text-zinc-300 hover:text-white hover:bg-white/5"
                  }`}
                >
                  {item.label}
                </Link>
              </li>
            ))}
          </ul>

          <div className="flex items-center gap-3">
            <Link to={"/contact"} className="hidden md:block">
              <button className="relative overflow-hidden font-chivo rounded-full px-6 py-2.5 bg-gradient-to-r from-brand-500 to-brand-700 text-sm font-semibold shadow-glow hover:shadow-[0_0_30px_-5px_rgba(225,29,72,0.8)] transition-all hover:scale-105">
                <span className="relative z-10 flex items-center gap-2">
                  Contact Us
                  <i className="fa-solid fa-arrow-right text-xs"></i>
                </span>
              </button>
            </Link>

            {/* Mobile toggle */}
            <button
              onClick={() => setToggleSidebar(!toggleSidebar)}
              className="md:hidden w-10 h-10 flex items-center justify-center rounded-full border border-white/10 bg-white/5 hover:bg-white/10 transition-colors"
              aria-label="Open menu"
            >
              <i className="fa-solid fa-bars"></i>
            </button>
          </div>
        </div>

        {/* Mobile sidebar */}
        <div
          className={`fixed inset-0 bg-black/95 backdrop-blur-xl z-50 md:hidden transition-all duration-300 ${
            toggleSidebar
              ? "opacity-100 pointer-events-auto"
              : "opacity-0 pointer-events-none"
          }`}
        >
          <div className="flex items-center justify-between px-5 py-4 border-b border-white/10">
            <img src={logo} alt="" className="h-14 object-contain" />
            <button
              className="w-10 h-10 flex items-center justify-center rounded-full border border-white/10 bg-white/5"
              onClick={() => setToggleSidebar(false)}
              aria-label="Close menu"
            >
              <i className="fa-solid fa-xmark"></i>
            </button>
          </div>

          <div className="flex flex-col gap-2 mt-8 px-6">
            {[...navItems, { to: "/contact", label: "Contact" }].map(
              (item, idx) => (
                <Link
                  key={item.to}
                  to={item.to}
                  onClick={() => setToggleSidebar(false)}
                  className={`flex items-center justify-between px-5 py-4 rounded-2xl border transition-all ${
                    isActive(item.to)
                      ? "bg-gradient-to-r from-brand-600 to-brand-800 border-transparent"
                      : "border-white/10 bg-white/[0.03] hover:border-brand-500/50"
                  }`}
                  style={{ animationDelay: `${idx * 60}ms` }}
                >
                  <span className="uppercase font-semibold tracking-wide text-sm">
                    {item.label}
                  </span>
                  <i className="fa-solid fa-arrow-right text-brand-400"></i>
                </Link>
              )
            )}
          </div>

          <div className="absolute bottom-8 left-0 right-0 px-6 flex flex-col gap-3 text-sm text-zinc-400">
            <a
              href="tel:+13475820864"
              className="flex items-center gap-3 hover:text-white"
            >
              <i className="fa-solid fa-phone text-brand-500"></i>
              (347) 582-0864
            </a>
            <a
              href="mailto:biniautorepairinc@gmail.com"
              className="flex items-center gap-3 hover:text-white"
            >
              <i className="fa-solid fa-envelope text-brand-500"></i>
              biniautorepairinc@gmail.com
            </a>
          </div>
        </div>
      </nav>
    </div>
  );
}

export default Header;
